<template>
    <div class="relative">

        <ProgrammeIntakeForm
            :selectLabel="selectLabel"
            :filter-label="filterLabel"
            :clear-all-filter-label="clearAllFilterLabel"
            :faculty-filter-label="facultyFilterLabel"
            :intake-filter-label="intakeFilterLabel"
            @search-terms-changed="searchTermsChangedHandler"
        ></ProgrammeIntakeForm>

        <div v-if="displayItems.length > 0 && !isBusy">
            <TransitionGroup
                enter-active-class="ease-out duration-300"
                leave-active-class="ease-in"
                enter-from-class="opacity-0 invisible -translate-y-2"
                enter-to-class="visible opacity-1 translate-y-0"
                leave-from-class="visible opacity-1"
                leave-to-class="invisible opacity-0">
                <ProgrammeIntakeListItem  :key="idx" :idx="idx" :data="item" v-for="(item, idx) in displayItems"></ProgrammeIntakeListItem>
            </TransitionGroup>
        </div>


        <div v-else-if="!isBusy" class="flex justify-center items-center p-12">
            <p class="body-4 text-brand-gray-200 text-center"> {{ resultNotFoundMessage }}</p>
        </div>

        <div v-if="isBusy" class="flex flex-col gap-3 mb-8">
            <SkeletonLoadingBar class="h-24"></SkeletonLoadingBar>
            <SkeletonLoadingBar class="h-24"></SkeletonLoadingBar>
            <SkeletonLoadingBar class="h-24"></SkeletonLoadingBar>
        </div>

        <div v-if="displayItems.length > 1 && isShowLazyLoad" ref="target" class="w-full z-10 pointer-events-none text-center flex justify-center items-center py-4">
            <div role="status">
                <svg aria-hidden="true" class="w-8 h-8 mr-2 text-gray-200 animate-spin  fill-brand-red" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                </svg>
            </div>
            <span class="body-3 text-black">加载中...</span>
        </div>

    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useIntakeStore } from '../../stores';
import ProgrammeIntakeForm from './programme-intake-form.vue';
import ProgrammeIntakeListItem from './programme-intake-list-item.vue';
import { computed, onMounted, ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { SkeletonLoadingBar } from '@/modules/core/components';
import { IntakeList } from './types';
import { UmbracoBaseContent } from "@/modules/website/typings";
import { seamlessScrollTo } from '@/modules/core/utilities';

interface Props {
    educationLevelList: UmbracoBaseContent[];
    intakeMonthsList: UmbracoBaseContent[];
    facultiesList: UmbracoBaseContent[];
    programmesList: IntakeList[];
    selectLabel: string;
    filterLabel: string;
    clearAllFilterLabel: string;
    resultNotFoundMessage: string;
    facultyFilterLabel: string;
    intakeFilterLabel: string;
    intakeAnchor: string;
}

const props = defineProps<Props>();

const intakeStore = useIntakeStore();
const { data, educationLevelAll, educationLevelList, intakeMonthsList, facultiesList, filteredItem, filters, isBusy } = storeToRefs(intakeStore);

const initialLoad = ref(true);
const target = ref(null);
const isVisible = ref(false);

const isShowLazyLoad = computed(() => {
    return (displayItems.value.length !== filteredItem.value.length) && filteredItem.value.length > 1;
});

const displayItems = computed(() => {
    const data = filteredItem.value.slice(0, (filters.value.currentPageIndex + 1) * filters.value.itemPerPage);
    return data;
})

const searchTermsChangedHandler = () => {
    seamlessScrollTo(props.intakeAnchor);
}

onMounted(() => {
    setTimeout(() => initialLoad.value = false, 1000);
})

useIntersectionObserver(
    target,
    ([{ isIntersecting }]) => {
        isVisible.value = isIntersecting;

        setTimeout(() => {
            if ((displayItems.value.length !== filteredItem.value.length) && !initialLoad.value) {
                filters.value.currentPageIndex++
            }
        }, 500)

    },
)

const mapToOptions = (list: UmbracoBaseContent[]) => list.map(item => ({
    value: item.key,
    label: item.name,
}));

const initialProps = () => {
    educationLevelList.value = mapToOptions(props.educationLevelList);
    intakeMonthsList.value = mapToOptions(props.intakeMonthsList);
    facultiesList.value = mapToOptions(props.facultiesList);

    data.value = props.programmesList;
    filters.value.educationLevelId = props.educationLevelList[0].key;
    educationLevelAll.value = props.educationLevelList[0];
}

initialProps()

</script>
